import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Sparkles, BarChart3 } from "lucide-react";
import ReportWidget from "@/components/reports/ReportWidget";
import WidgetEditDialog from "@/components/reports/WidgetEditDialog";
import ReportAiChat from "@/components/reports/ReportAiChat";

export default function Reports() {
  const [editing, setEditing] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const { data } = useQuery({ queryKey: ["report-widgets"], queryFn: async () => (await api.get("/reports/widgets")).data });
  const widgets = data?.widgets || [];

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports & Analytics"
        subtitle="A fully customisable dashboard — edit or remove any widget, or ask the AI to build new reports from any Compass data."
        breadcrumb={["System", "Reports"]}
        actions={
          <Button onClick={() => setChatOpen(true)} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="ai-report-chat-btn">
            <Sparkles size={15} className="mr-1" />AI Report Builder
          </Button>
        }
      />
      <div className="px-6 lg:px-10 py-6">
        {widgets.length === 0 ? (
          <Card className="rounded-sm border-slate-200 industrial-shadow p-10 text-center" data-testid="reports-empty">
            <BarChart3 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No report widgets yet.</p>
            <p className="text-xs text-slate-400 mt-1">Use the AI Report Builder to create your first report from live data.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-grid">
            {widgets.map((w) => <ReportWidget key={w.id} widget={w} onEdit={setEditing} />)}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-4">
          Shared team dashboard — edits apply for everyone with Reports access. Staff pick PUBLIC widgets for their own
          My Dashboard page; MGMT widgets stay visible to Management access only.
        </p>
      </div>
      {editing && <WidgetEditDialog widget={editing} onClose={() => setEditing(null)} hasMgmt={!!data?.has_mgmt} />}
      {chatOpen && <ReportAiChat onClose={() => setChatOpen(false)} hasMgmt={!!data?.has_mgmt} />}
    </div>
  );
}
